import {Compiler, Injectable} from '@angular/core';
import {User} from './user';
import {Router} from '@angular/router';
import {LoadingService} from '../loading/loading.service';
import {BehaviorSubject, Observable, of, throwError, share} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {DataService} from '../data.service';
import {distinctUntilChanged} from 'rxjs/operators';
import {api_error, Param} from '../../shared/common-components/AppConstants';
import {AlertService} from '../alert/alert.service';
import {UserSelectionService} from '../../shared/user-selection/user-selection-service';
import {RoutingStateService} from '../routing-state.service';
import {BidCycleService} from '../../features/hhg-rate-duns/bidcycle.service';

@Injectable()
export class UserService extends DataService {
  private tmssloader;
  // initiating user for the first time
  userInit: any;
  paramValue: any;
  private user: User = null;
  private keepAliveFlag = false;
  // @ts-ignore
  private userSource = new BehaviorSubject<User>(this.userInit);
  // @ts-ignore
  private paramSource = new BehaviorSubject<Param>(this.paramValue);
  currentUser = this.userSource.asObservable().pipe(distinctUntilChanged());
  currentParams = this.paramSource.asObservable().pipe(distinctUntilChanged());

  private baseURI = this.apiUri + '/user';

  private userObservable$: Observable<any>;

  constructor(protected http: HttpClient, private _compiler: Compiler, private router: Router, private loader: LoadingService,
              private alertService: AlertService, private userSelectionService: UserSelectionService, private routingService: RoutingStateService,
              private bidcleService: BidCycleService) {
    super(http);
    this.tmssloader = this.loader;
  }

  // using this to avoid local storage
  setUserObservable(user: User) {
    this.userSource.next(user);
    this.keepAliveFlag = true; // set keep alive to true when current user is initialized
  }

  // using this to avoid local storage
  getCurrentUserObservable() {
    return this.currentUser;
  }

  setParamObservable(param: Param) {
    this.paramSource.next(param);
  }

  // using this to avoid local storage
  getCurrentParamObservable() {
    return this.currentParams;
  }

  /*this approach should use for first time calls to service */
  getUserDetails(): Observable<any> {
    this.userObservable$ = this.http.get(this.baseURI);
    return this.userObservable$;
  }

  // handling error
  errorHandler(error: any): any {
    if (error.status < 400) {
      return throwError(new Error(error.status));
    }
  }

  /*using this approach to avoid multiple calls to service */
  getCurrentUserDetails(): Observable<User> {
    if (this.userObservable$) {
      return this.userObservable$;
    } else {
      this.setUri(this.baseURI);
      this.userObservable$ = this.get().pipe(share());
      return this.userObservable$;
    }
  }

  getUserByUserId(userId: number): Observable<any> {
    const usersListUrl = this.baseURI + '/' + userId;
    this.setUri(usersListUrl);
    return this.get();
  }

  /*
  * logout procedure
  * Returns to home page
  */
  logout(from?) {
    this.routingService.clearBreadCrumbs();
    this.tmssloader.show();
    localStorage.removeItem('currentUser');
    this.currentUser = null;
    localStorage.clear();
    sessionStorage.clear();
    this._compiler.clearCache();
    this.setUri('tmssserver/logout'); // this logout should have /logout
    // logout on the server side
    this.userObservable$ = null;
    this.http.get('/tmssserver/logout').subscribe(response => {
        this.tmssloader.hide();
        if (from === 'reset password') {
          this.alertService.success('Password reset is successful. Please Login Again!!');
        } /* else {
          this.alertService.success('user successfully logged out');
        }*/
      },
      err => {
        this.alertService.error(api_error);
      }
    );
    this.keepAliveFlag = false; // set keepalive to false when current user is logged out
    setTimeout(() => {
      // this.user$ = null;
      this.user = null;
      this._compiler.clearCache();
      localStorage.clear();
      sessionStorage.clear();
      localStorage.removeItem('currentUser');
      this.tmssloader.hide();
    }, 2000);
    if (from !== 'okta') {
      this.router.navigate(['/signon']);
    }
  }

  /**
   * Pings the server to ensure user session hasn't expired
   *
   */
  keepAlive() {
    // if user is logged in
    if (this.keepAliveFlag) {
      // ping the server the server side
      return this.http.post(this.baseURI + '/keepAlive', null).subscribe(response => {
        },
        err => {
        }
      );
    }
  }

  isRateFilingClosed() {
    let closingDate: number;
    // const expiryTime = 1616464800; // unix timestamp for March 22, 2021 10 PM
    this.bidcleService.Data.subscribe(result => {
      (result) ?
        closingDate = Date.parse(result.toString()) : null;
    });
    return of(Date.now() > closingDate);
  }

  hasRateFilingClosed(expTime: number) {
    return of(Date.now() > expTime);
  }

  /**
   * removing session
   */
  removeSession(currentUser: User) {
    this.routingService.clearBreadCrumbs();
    this.userSelectionService.removeUserSession(currentUser.id, currentUser.jSessionId).subscribe(respn => {
    }, (error1) => {
      // this.alertService.info('unable to remove current session');
    });
  }

  /**
   * removing session ByUserId
   */
  removeUserSessionByUserId(currentUser: User) {
    this.routingService.clearBreadCrumbs();
    this.userSelectionService.removeUserSessionByUserId(currentUser.id).subscribe((respn: any) => {
      console.log(respn);
    }, (error1) => {
      this.alertService.info('unable to remove current session');
    });
  }

  getSysLoginReport(agencyMasterId: any): Observable<any> {
    this.setUri(this.apiUri + '/sysLoginHistory/' + agencyMasterId + '/agencyMasterId');
    return this.get();
  }

  getTspLoginReport(carrierId: any): Observable<any> {
    this.setUri(this.apiUri + '/tspLoginHistory/' + carrierId + '/carrierId');
    return this.get();
  }

  getAgencyLoginReport(orgId: any): Observable<any> {
    this.setUri(this.apiUri + '/agencyLoginHistory/' + orgId + '/orgId');
    return this.get();
  }

  resetMfa(emailId: string): Observable<any> {
    return this.http.put(this.baseURI + '/' + emailId, {});
  }

  getBroadcastMsg(): Observable<any> {
    this.setUri(this.publicApiUri + '/broadcastmessages/getall');
    return this.get();
  }

  getBroadcastMsgByBusinessType(businessType: string): Observable<any> {
    this.setUri(this.publicApiUri + '/broadcastmessages/getAllByBusinessType/' +businessType);
    return this.get();
  }

  getAgencyOrTSPList(permission: string, selectedValue: string): Observable<any>{
    this.setUri(this.apiUri + `/usercertification/getagencyortspview/${permission}/${selectedValue}`);
    return this.get();
  }

  updateUserCertify(listPayloadEntity:any[]): Observable<any>{
    return this.http.post<any>(this.apiUri + '/usercertification/updateusercertification', listPayloadEntity, {responseType: 'text' as 'json'});
  }

  deleteUsersCertify(listPayloadEntity:any[]): Observable<any> {
    return this.http.post<any>(this.apiUri + '/usercertification/deleteusercertification', listPayloadEntity, {responseType: 'text' as 'json'});
  }

}
